import React from "react";
import { Box, Grid, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { FormProvider } from "../../../common/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup"
import { apiConstants, postGeneric } from "../../../common/constants";
import { useNavigate } from "react-router-dom";
import { useUnsavedChangesBlocker } from "../../../helpers/UtilityHelper";
import CustomerDetails from "./CustomerDetails"
import PickupLocation from "./PickupLocation"
import OrderDetails from "./OrderDetails";
import PackageDetails from "./PackageDetails";
import { useGetMerchantQuery } from "../../../redux/commonRTK";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import PageTitle from "../../../common/PageTitle";
import { useSnackbar } from "notistack";
import SelectCourier from "./Courier";
import { RTO_DEFAULT_VALUES, RTO_FORM_FIELDS, buildReturnDetails, rtoLocationRule } from "../RtoAddressSelector";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, BORDER, WHITE, PRIMARY_BLUE, SUBTLE } from '../../../styles/foundation/colors';
import {
    pageContainerSx, pageHeroSx, heroTitleSx, heroSubSx,
    stepperCardSx, stepTitleSx, helpTextSx, helpTextTypeSx,
} from '../../../styles/patterns';

const steps = [
    "Customer Details",
    "Pickup Details",
    "Order Details",
    "Package Details",
    "Select Courier",
];

// `embedded` is set when this form is rendered inside the tabbed Create Shipment page,
// which already supplies the page container — avoids doubling the page padding.
function CreateB2COrder({ embedded = false }) {
    const merchant = getMerchant();
    const [activeStep, setActiveStep] = React.useState(0);
    const navigate = useNavigate();
    const [creatingOrder, setIsCreatingOrder] = React.useState(false);
    const { data } = useGetMerchantQuery({ id: merchant.id });
    const pickupLocations = data?.pickup_details || [];
    const { enqueueSnackbar } = useSnackbar();

    const methods = useForm({
        defaultValues: {
            reference: '',
            payment_mode: 'PREPAID',
            order_type: 'Forward Shipment',
            delivery_details: {
                to_phone_number: '', to_name: '', to_email: '',
                to_pincode: '', to_city: '', to_state: '', to_address: '',
            },
            client_order_id: '', invoice_value: '', invoice_number: '',
            shipping_charge: '', transaction_charge: '', giftwrap_charge: '',
            courier_type: 'Prozo Choice',
            shipment_detail: [{ item_weight: '', item_length: '', item_breadth: '', item_height: '' }],
            packageId: '',
            ...RTO_DEFAULT_VALUES,
        },
        resolver: yupResolver(
            Yup.object().shape({
                delivery_details: Yup.object().shape({
                    to_phone_number: Yup.string()
                        .required("Please enter your phone number.")
                        .matches(/^\d{10}$/, "Your phone number must be exactly 10 digits long."),
                    to_name: Yup.string().required("Please enter your name."),
                    to_pincode: Yup.string()
                        .required("Please enter your pincode.")
                        .matches(/^\d{6}$/, "Pincode must be exactly 6 digits long."),
                    to_city: Yup.string().required("Please enter your city."),
                    to_state: Yup.string().required("Please enter your state."),
                    to_address: Yup.string().required("Please enter your address."),
                }),
                item_list: Yup.array().of(
                    Yup.object().shape({
                        item_name: Yup.string().required("Please enter the item name."),
                        units: Yup.number().min(1, "Quantity must be at least 1.").required("Please enter the quantity."),
                        sku_id: Yup.string().required("Please enter the SKU ID."),
                        selling_price: Yup.number()
                            .transform((value, originalValue) => { let s = originalValue != null ? String(originalValue).trim() : ""; return s === "" ? null : value; })
                            .nullable().min(1, "Selling price must be at least Rs. 1.").required("Please enter the selling price."),
                        discount: Yup.number().optional(),
                        tax: Yup.number().optional(),
                        hsn: Yup.string().optional(),
                    })
                ),
                cod_amount: Yup.number().when('payment_mode', {
                    is: val => val === 'COD',
                    then: Yup.number()
                        .transform((value, originalValue) => { const s = String(originalValue).replace(/[\s]+/g, ''); return s.match(/^[0-9]*$/) ? Number(s) : null; })
                        .typeError("COD amount must be a numeric value.")
                        .required("COD amount is required when payment mode is COD.")
                        .min(1, "COD amount must be at least Rs. 1.")
                        .max(Yup.ref('invoice_value'), "COD amount cannot be greater than the invoice value.")
                }),
                rvp_reason: Yup.string().when('order_type', {
                    is: val => val === 'Reverse Shipment',
                    then: Yup.string().required("RVP reason is mandatory for Reverse Shipment."),
                }),
                payment_mode: Yup.string().required("Please select a payment mode."),
                reference: Yup.string().required("Please enter a reference number."),
                pickupLocation: Yup.string().required("Please enter the pickup location."),
                rtoLocation: rtoLocationRule(Yup),
                invoice_value: Yup.number()
                    .transform((value, originalValue) => { let s = originalValue != null ? String(originalValue).trim() : ""; return s === "" ? null : value; })
                    .nullable().min(1, "Invoice amount must be at least Rs. 1.").required("Please enter the invoice value."),
                invoice_number: Yup.string().optional(),
                shipping_charge: Yup.string().optional(),
                transaction_charge: Yup.string().optional(),
                giftwrap_charge: Yup.string().optional(),
                shipment_detail: Yup.array().of(
                    Yup.object().shape({
                        item_weight: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Weight must be at least 1 gram.").required("Weight is required."),
                        item_length: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Length must be at least 1 cm.").required("Length is required."),
                        item_breadth: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Breadth must be at least 1 cm.").required("Breadth is required."),
                        item_height: Yup.number().transform((v, o) => { let s = o != null ? String(o).trim() : ""; return s === "" ? null : v; }).nullable().min(1, "Height must be at least 1 cm.").required("Height is required.")
                    })
                ).required("Please provide shipment details.")
            })
        )
    });

    const { handleSubmit, formState: { isDirty }, reset } = methods;
    const { BlockerDialog } = useUnsavedChangesBlocker(isDirty, reset);

    const onSubmit = (formData) => {
        setIsCreatingOrder(true);
        const { dispatch_mode, ...restFormData } = formData;
        let request = { ...restFormData, skipRule: false, merchantOId: merchant.id, ...(dispatch_mode !== "ANY" ? { dispatch_mode } : {}) };

        const selectedAddress = pickupLocations.find(loc => loc.id === request.pickupLocation);
        const pickup_details = {
            from_address: selectedAddress.address_line, from_city: selectedAddress.city,
            from_country: selectedAddress.country, from_name: selectedAddress.name,
            from_pincode: selectedAddress.pincode, from_state: selectedAddress.state,
            from_phone_number: request.pickupContactPhone || selectedAddress.contactNumber,
            from_email: request.pickupContactEmail || selectedAddress.email,
            gstin: selectedAddress.gstin
        }
        delete request.pickupContactPhone;
        delete request.pickupContactEmail;

        // Optional RTO address: only sent when the merchant picked a different one.
        const return_details = buildReturnDetails(request, pickupLocations);
        RTO_FORM_FIELDS.forEach((field) => delete request[field]);

        if (!request.packageId) delete request.packageId
        if (request.payment_mode === "PREPAID") request.cod_amount = 0;
        request.skipRule = request.courier_type === "Other";
        if (request.courier_type !== "Other") { delete request.courierPartner; }
        else { request.courier_id = { prozoId: request.courierPartner }; delete request.courierPartner; }
        request.shippingLabelId = request.shippingLabelId || request.reference;
        request.channel_name = request.channel_name || 'WMS';

        request = { ...request, pickup_details, ...(return_details ? { return_details } : {}) };
        request.delivery_details.to_country = 'IN';
        request.is_reverse = false;

        postGeneric(apiConstants.CREATE_ORDER, request)
            .then(() => {
                enqueueSnackbar("Successfully Created!", { variant: 'success' });
                reset();
                if (request.order_type === "Forward Shipment") navigate("/orders");
                else navigate("/reverse-orders");
            }).catch((err) => {
                const message = err?.response?.data?.message || err.message;
                enqueueSnackbar(message || "Error creating order", { variant: "error" });
            }).finally(() => setIsCreatingOrder(false))
    }

    return (
        <Box sx={embedded ? undefined : pageContainerSx}>
            <PageTitle subTitle="Fill in customer, pickup, order, and package details to book a new D2C shipment">Create Forward Shipment</PageTitle>

            {/* Help text */}
            <Box sx={helpTextSx}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                <Typography sx={helpTextTypeSx}>
                    <strong>How it works:</strong> Complete each step in order — customer address, pickup location, order & package details,
                    then select a courier. PACE engine will auto-allocate the best courier unless you choose manually.
                </Typography>
            </Box>

            <Grid container spacing={2.5} sx={{ alignItems: 'flex-start' }}>
                {/* Stepper sidebar */}
                <Grid item xs={12} md={2.5}>
                    <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', p: 2 }}>
                        <Stepper orientation="vertical" activeStep={activeStep}
                            sx={{
                                '& .MuiStepLabel-label': { fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.medium },
                                '& .MuiStepLabel-label.Mui-active': { fontWeight: WEIGHT.bold, color: PRIMARY_BLUE },
                                '& .MuiStepLabel-label.Mui-completed': { color: SUBTLE },
                                '& .MuiStepIcon-root.Mui-active': { color: PRIMARY_BLUE },
                            }}
                        >
                            {steps.map((label) => (
                                <Step key={label}><StepLabel>{label}</StepLabel></Step>
                            ))}
                        </Stepper>
                    </Box>
                </Grid>

                {/* Form area */}
                <Grid item xs={12} md={9.5}>
                    <Box sx={stepperCardSx}>
                        <Typography sx={stepTitleSx}>{activeStep + 1}. {steps[activeStep]}</Typography>
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            {activeStep === 0 && <CustomerDetails setActiveStep={setActiveStep} />}
                            {activeStep === 1 && <PickupLocation setActiveStep={setActiveStep} />}
                            {activeStep === 2 && <OrderDetails setActiveStep={setActiveStep} />}
                            {activeStep === 3 && <PackageDetails creatingOrder={creatingOrder} setActiveStep={setActiveStep} />}
                            {activeStep === 4 && (
                                <SelectCourier creatingOrder={creatingOrder} setActiveStep={setActiveStep}
                                    formData={methods.getValues()} pickupLocations={pickupLocations} merchantId={merchant.id}
                                />
                            )}
                        </FormProvider>
                    </Box>
                </Grid>
            </Grid>
            <BlockerDialog />
        </Box>
    )
}

export default CreateB2COrder;
